/**
 * What "the plan check could not run" is, and how a caller recognises it.
 *
 * `PlanUpdate.checkForPlanAdjustments` answers an internal crash with a FAILED result rather than
 * by throwing. Before #5776 that result was a flat object whose every field except `error` still
 * carried a plausible-looking value -- `needsAdjustment` false, `message` usually `AllOk` -- so a
 * caller reading only those fields rendered a failed check as the POSITIVE verdict, telling the
 * user they were on track when nothing had worked that out (df-5ba092626ea2). The failed branch
 * now carries none of those fields.
 *
 * `SvPlanHandlerService.checkForPlanAdjustments` turns that failed result into the error below,
 * so its callers are handed either a real verdict or an exception, never a failed result.
 *
 * Kept free of Angular so a unit test can drive it (`plan-check-failure.test.ts`), the same way
 * `weekly-checkup-outcome.ts` and `weekly-checkup-steps.ts` are.
 */

/**
 * Thrown by `SvPlanHandlerService.checkForPlanAdjustments` when the plan solver could not work
 * out an answer. It has already been reported to Sentry under
 * `plan-update-check-for-adjustments-failed` by the time this is thrown, so a handler that only
 * wants to stop showing a verdict does not need to report it a second time.
 *
 * A class of its own rather than a plain `Error` for exactly that reason: `isPlanCheckFailed`
 * lets a caller tell "the check could not run, and we already know about it" apart from any
 * other failure caught by the same `try`, which it must still let through.
 */
export class PlanCheckFailedError extends Error {
  constructor(solverError: string) {
    super(`The plan check could not be completed: ${solverError}`);
    this.name = 'PlanCheckFailedError';
    // restores the prototype chain across the ES5 target's Error subclassing, so `instanceof`
    // below answers correctly in the built bundle as well as under ts-node
    Object.setPrototypeOf(this, PlanCheckFailedError.prototype);
  }
}

/** True when `err` is the failure `checkForPlanAdjustments` throws. Never test the message text. */
export function isPlanCheckFailed(err: unknown): err is PlanCheckFailedError {
  return err instanceof PlanCheckFailedError;
}

